import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ConfigureServices, setIsApiConfigured } from '@invision/core';
import { getEnvironmentDetails } from './environment.helpers';
import { DEFAULT_DEVELOPMENT_ENVIRONMENT } from '../constants';
import { AuthenticationStorage } from './authenticationStorage';
import type { EnvironmentValue } from '../constants';

export function useAuthenticationApiConfiguration(): void {
  const dispatch = useDispatch();

  useEffect(() => {
    configureAuthenticationApi();
    dispatch(setIsApiConfigured(true));
  }, [dispatch]);
}

export function configureAuthenticationApi(
  environment?: EnvironmentValue
): void {
  const { isDevelopment, detectedEnvironment } = getEnvironmentDetails();

  // Read environment from legacy storage first (takes precedence for consistency)
  const storedEnvironment = AuthenticationStorage.getSelectedEnvironment();
  const fallbackEnvironment = isDevelopment
    ? storedEnvironment || DEFAULT_DEVELOPMENT_ENVIRONMENT
    : detectedEnvironment || DEFAULT_DEVELOPMENT_ENVIRONMENT;
  const targetEnvironment = environment || fallbackEnvironment;

  // Store detected environment in session storage (only if not development)
  if (!isDevelopment && targetEnvironment) {
    AuthenticationStorage.setSelectedEnvironment(targetEnvironment);
  }

  // Read existing session data from storage for app refresh scenarios
  const existingSessionId = AuthenticationStorage.getSessionId() || '';
  const existingSystemId = AuthenticationStorage.getSystemId() || '';

  ConfigureServices({
    apiKey: '',
    environment: { name: targetEnvironment, value: targetEnvironment },
    preferences: {},
    sessionId: existingSessionId,
    systemId: existingSystemId
  });
}

/**
 * Drops the session from the in-memory services client so no session token is
 * attached to subsequent unauthenticated requests (e.g. RetrieveIdentity
 * Provider on the login page).
 *
 * Removing the session id from storage is not sufficient on its own: the core
 * service client caches the session id in memory (set via `ConfigureServices`
 * at app bootstrap), and that cached value survives client-side navigation such
 * as logout -> login. This re-runs `ConfigureServices` with an empty session
 * while preserving the resolved environment, ensuring a stale session cannot
 * leak into later requests.
 */
export function clearAuthenticationApiSession(): void {
  const { isDevelopment, detectedEnvironment } = getEnvironmentDetails();

  const storedEnvironment = AuthenticationStorage.getSelectedEnvironment();
  const targetEnvironment =
    (isDevelopment ? storedEnvironment : detectedEnvironment) ||
    DEFAULT_DEVELOPMENT_ENVIRONMENT;

  ConfigureServices({
    apiKey: '',
    environment: { name: targetEnvironment, value: targetEnvironment },
    preferences: {},
    sessionId: '',
    systemId: ''
  });
}
